import { Box, Typography, useTheme } from '@material-ui/core';
import * as React from 'react';
import AlertBase from '../../../components/Alerts/AlertBase';
import FormikFormattedTextField from '../../../components/FormikInputWrappers/FormikFormattedTextField';
import FormikSelectInput from '../../../components/FormikInputWrappers/FormikSelectInput';
import { CssSpreadType } from '../../../utils/css-spread-type';
import { useNonPaymentReasonFormContext } from '../NonPaymentReasonFormContext';
import useDictionary from '../../../dictionaries/context/useDictionary';

export type TermExtensionSoxFormRef = {
  initialValue: Record<string, string | number>,
  validation: {
    rules: Record<string, string>,
    messages: Record<string, string>
  },
};

const SOX_MAIN_ECONOMIC_OPTIONS = [
  { label: 'Asalariado', value: 'asalariado' },
  { label: 'Pensionado', value: 'pensionado' },
  { label: 'Comerciante', value: 'comerciante' },
  { label: 'Agropecuario', value: 'agropecuario' },
  { label: 'Manufactura', value: 'manufactura' },
  { label: 'Rentista de Capital', value: 'rentista' },
  { label: 'Servicios', value: 'servicios' },
  { label: 'Transportador', value: 'transportador' },
  { label: 'Profesional Independiente', value: 'independiente' },
  { label: 'Otras actividades de servicios personales n.c.p', value: 'otras' },
];

const SOX_EXTRA_ECONOMIC_OPTIONS = [
  { label: 'Sin actividades económicas adicionales', value: 'NO_REFIERE' },
  { label: 'Asalariado', value: 'asalariado' },
  { label: 'Pensionado', value: 'pensionado' },
  { label: 'Comerciante', value: 'comerciante' },
  { label: 'Agropecuario', value: 'agropecuario' },
  { label: 'Manufactura', value: 'manufactura' },
  { label: 'Servicios', value: 'servicios' },
  { label: 'Rentista de Capital', value: 'rentista' },
  { label: 'Transportador', value: 'transportador' },
  { label: 'Profesional Independiente', value: 'independiente' },
  { label: 'Otras actividades de servicios', value: 'otras_adicionales' },
];

const SOX_INITIAL_VALUES: Record<string, string | number> = {
  economic: '',
  salary: '',
  monthlyPayments: '',
  extraIncomeEconomic: 'NO_REFIERE',
  extraIncome: '',
};

const SOX_VALIDATION_RULES: Record<string, string> = {
  economic: 'required',
  salary: 'required|numeric|min:1',
  monthlyPayments: 'required|numeric|min:0',
  extraIncomeEconomic: 'required',
  extraIncome: 'required_unless:extraIncomeEconomic,NO_REFIERE|numeric|min:0',
};

interface TermExtensionSoxFormProps {
  formik: any;
}

const TermExtensionSoxForm = React.forwardRef<TermExtensionSoxFormRef, TermExtensionSoxFormProps>(
  ({ formik }, ref) => {
    const theme = useTheme();
    const nonPaymentReasonFormContext = useNonPaymentReasonFormContext();
    const { setErrors } = nonPaymentReasonFormContext || {};
    const { gT } = useDictionary();

    const hasExtraActivity = formik.values.extraIncomeEconomic !== 'NO_REFIERE'
      && formik.values.extraIncomeEconomic !== '';

    const salaryValue = parseInt(formik.values.salary, 10);
    const showSalaryZeroAlert = formik.touched?.salary && salaryValue === 0;

    const validationMessages = React.useMemo<Record<string, string>>(() => ({
      'required.economic': gT('termExtensionSox_economic_required_error'),
      'required.salary': gT('termExtensionSox_salary_required_error'),
      'min.salary': gT('termExtensionSox_salary_min_error'),
      'required.monthlyPayments': gT('termExtensionSox_monthlyPayments_required_error'),
      'required.extraIncomeEconomic': gT('termExtensionSox_extraIncomeEconomic_required_error'),
      'required_unless.extraIncome': gT('termExtensionSox_extraIncome_required_error'),
      'min.extraIncome': gT('termExtensionSox_extraIncome_min_error'),
    }), [ gT ]);

    React.useImperativeHandle(ref, () => ({
      initialValue: SOX_INITIAL_VALUES,
      validation: {
        rules: SOX_VALIDATION_RULES,
        messages: validationMessages,
      },
    }), [ validationMessages ]);

    // Block form submission via context when extraIncome is required but missing
    React.useEffect(() => {
      const contextErrors: Record<string, string> = {};
      if (hasExtraActivity) {
        const extraIncomeNum = parseInt(formik.values.extraIncome, 10);
        if (!formik.values.extraIncome || Number.isNaN(extraIncomeNum) || extraIncomeNum < 0) {
          contextErrors.extraIncome = gT('termExtensionSox_extraIncome_required_error');
        }
      }
      setErrors?.(contextErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ hasExtraActivity, formik.values.extraIncome, formik.values.extraIncomeEconomic ]);

    const sectionTitleStyle = {
      ...(theme.typography.body1 as CssSpreadType),
      color: theme.palette.indigo.main,
      fontWeight: 600,
    };

    return (
      <Box mt={2}>
        <Typography variant="h5" className="mb-1" style={sectionTitleStyle}>
          {gT('termExtensionSox_title')}
        </Typography>
        <Typography variant="body2" className="mb-2">
          {gT('termExtensionSox_subtitle')}
        </Typography>

        {/* 1. Actividad económica principal */}
        <Typography variant="body2" className="mb-1">
          {gT('termExtensionSox_economic_description')}
        </Typography>
        <FormikSelectInput
          fullWidth
          className="mb-2"
          formik={formik}
          valueId="economic"
          label={gT('termExtensionSox_economic_label')}
          options={SOX_MAIN_ECONOMIC_OPTIONS}
        />

        {/* 2. Salario, ingreso o pensión */}
        <Typography variant="body2" className="mb-1">
          {gT('termExtensionSox_salary_description')}
        </Typography>
        <FormikFormattedTextField
          fullWidth
          className="mb-1"
          formik={formik}
          valueId="salary"
          label={gT('termExtensionSox_salary_label')}
          placeholder="$0"
          currency
        />
        {showSalaryZeroAlert && (
          <AlertBase type="informative" className="mb-2">
            {gT('termExtensionSox_salary_zero_alert')}
          </AlertBase>
        )}

        {/* 3. Cuotas de crédito mensuales - Sector financiero */}
        <Typography variant="body2" className="mb-1">
          {gT('termExtensionSox_monthlyPayments_description')}
        </Typography>
        <FormikFormattedTextField
          fullWidth
          className="mb-2"
          formik={formik}
          valueId="monthlyPayments"
          label={gT('termExtensionSox_monthlyPayments_label')}
          placeholder="$0"
          currency
        />

        {/* 4. Actividades económicas adicionales */}
        <Typography variant="body2" className="mb-1">
          {gT('termExtensionSox_extraIncomeEconomic_description')}
        </Typography>
        <FormikSelectInput
          fullWidth
          className="mb-2"
          formik={formik}
          valueId="extraIncomeEconomic"
          label={gT('termExtensionSox_extraIncomeEconomic_label')}
          options={SOX_EXTRA_ECONOMIC_OPTIONS}
        />

        {/* 5. Ingresos adicionales (conditional) */}
        {hasExtraActivity && (
          <>
            <Typography variant="body2" className="mb-1">
              {gT('termExtensionSox_extraIncome_description')}
            </Typography>
            <FormikFormattedTextField
              fullWidth
              className="mb-2"
              formik={formik}
              valueId="extraIncome"
              label={gT('termExtensionSox_extraIncome_label')}
              placeholder="$0"
              currency
            />
          </>
        )}
      </Box>
    );
  },
);

export default TermExtensionSoxForm;
